import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Edit2, Save, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachProfile = ({ coach, onProfileUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    phone: "",
    specialty: "",
    bio: "",
    photo_url: ""
  });

  useEffect(() => {
    if (coach) {
      setProfileData({
        name: coach.name || "",
        phone: coach.phone || "",
        specialty: coach.specialty || "",
        bio: coach.bio || "",
        photo_url: coach.photo_url || ""
      });
    }
  }, [coach]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/coach-auth/profile`, profileData, { withCredentials: true });
      toast.success("Profil mis à jour avec succès");
      setEditing(false);
      onProfileUpdated();
    } catch (err) {
      console.error("Failed to update profile:", err);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-1 lg:grid-cols-3 gap-8"
    >
      {/* Profile Card */}
      <div className="lg:col-span-1">
        <div className="bg-[#0F0F16] border border-white/10 p-6 text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <img
              src={profileData.photo_url || "https://images.unsplash.com/photo-1701481063068-0103e348d844?w=300"}
              alt={profileData.name}
              className="w-full h-full object-cover rounded-full"
            />
          </div>
          <h2 className="font-['Outfit'] text-xl font-bold">{coach.name}</h2>
          <p className="text-[#0066FF] text-sm font-semibold mt-1">{coach.specialty}</p>
          <p className="text-[#94A3B8] text-sm mt-2">{coach.diploma}</p>

          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-[#64748B] text-xs uppercase tracking-wider mb-2">Statut</p>
            <span className="inline-flex items-center px-3 py-1 bg-green-500/20 text-green-500 text-sm font-semibold">
              Actif
            </span>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div className="lg:col-span-2">
        <div className="bg-[#0F0F16] border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-['Outfit'] text-lg font-bold uppercase">
              Informations du profil
            </h3>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-2 text-[#0066FF] hover:text-[#3B82F6] transition-colors"
                data-testid="edit-profile-button"
              >
                <Edit2 className="w-4 h-4" />
                Modifier
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  className="flex items-center gap-1 text-[#94A3B8] hover:text-white transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                  Annuler
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={loading}
                  className="flex items-center gap-1 text-green-500 hover:text-green-400 transition-colors text-sm"
                  data-testid="save-profile-button"
                >
                  <Save className="w-4 h-4" />
                  Enregistrer
                </button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Nom complet</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  disabled={!editing}
                  className="bg-[#05050A] border-white/10 disabled:opacity-60"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="form-label">Téléphone</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  disabled={!editing}
                  className="bg-[#05050A] border-white/10 disabled:opacity-60"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>

            <div>
              <Label className="form-label">Spécialité</Label>
              <Input
                value={profileData.specialty}
                onChange={(e) => setProfileData({ ...profileData, specialty: e.target.value })}
                disabled={!editing}
                className="bg-[#05050A] border-white/10 disabled:opacity-60"
                data-testid="profile-specialty-input"
              />
            </div>

            <div>
              <Label className="form-label">URL de la photo</Label>
              <Input
                value={profileData.photo_url}
                onChange={(e) => setProfileData({ ...profileData, photo_url: e.target.value })}
                disabled={!editing}
                placeholder="https://..."
                className="bg-[#05050A] border-white/10 disabled:opacity-60"
                data-testid="profile-photo-input"
              />
            </div>

            <div>
              <Label className="form-label">Bio / Description</Label>
              <Textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                disabled={!editing}
                rows={4}
                className="bg-[#05050A] border-white/10 disabled:opacity-60 resize-none"
                data-testid="profile-bio-input"
              />
            </div>

            <div className="pt-4 border-t border-white/10">
              <p className="text-[#64748B] text-sm">
                <strong>Email :</strong> {coach.email}
              </p>
              <p className="text-[#64748B] text-sm mt-1">
                <strong>Diplôme :</strong> {coach.diploma}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CoachProfile;
